import React, { useEffect } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { getLeads, getContacts } from "../../slices/thunks";
import { getLoggedinUser } from "../../helpers/api_helper";
import Section from "./Section";
import RecentActivity from "./RecentActivity";
import getChartColorsArray from "../../Components/Common/ChartsDynamicColor";

const DashboardEcommerce = () => {
  document.title = "CRM Expert Dashboard | Leads & Contacts";

  const dispatch = useDispatch();
  const crm = useSelector((state) => state.Crm || {});
  const leadItems = Array.isArray(crm.leads) ? crm.leads : [];
  const contactItems = Array.isArray(crm.crmcontacts) ? crm.crmcontacts : [];

  const totalLeads = leadItems.length;
  const newLeadsCount = leadItems.filter((item) => Array.isArray(item.tags) && item.tags.includes("New")).length;
  const qualifiedLeadsCount = leadItems.filter((item) => Number(item.score) >= 70).length;
  const lostLeadsCount = leadItems.filter((item) => Number(item.score) > 0 && Number(item.score) <= 35).length;

  const totalContacts = contactItems.length;
  const phoneContacts = contactItems.filter((item) => item.phone).length;
  const taggedContacts = contactItems.filter((item) => Array.isArray(item.tags) && item.tags.length).length;
  const highScoreContacts = contactItems.filter((item) => Number(item.lead_score) >= 75).length;

  useEffect(() => {
    if (!leadItems.length) {
      dispatch(getLeads());
    }
    if (!contactItems.length) {
      dispatch(getContacts());
    }
  }, [dispatch, leadItems.length, contactItems.length]);

  const authUser = getLoggedinUser();
  const userName = authUser?.data?.first_name || authUser?.data?.name || authUser?.user?.first_name || authUser?.user?.name || authUser?.user?.username || authUser?.name || authUser?.username || authUser?.email || "Admin";

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const chartColors = getChartColorsArray('["--vz-primary", "--vz-success", "--vz-warning", "--vz-danger"]');
  const leadTrendSeries = [
    {
      name: "Leads",
      data: [totalLeads, newLeadsCount, qualifiedLeadsCount, lostLeadsCount, totalLeads + 1, totalLeads + 2, totalLeads + 3],
    },
  ];
  const leadTrendOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: [chartColors[0]],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.55,
        opacityTo: 0.0,
        stops: [50, 95, 100],
      },
    },
    xaxis: {
      categories: ["Contacts", "New", "Qualified", "Lost", "Follow-up", "Engaged", "Growth"],
      labels: { style: { colors: "#98a6ad" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: "#98a6ad" } } },
    grid: { borderColor: "#e9ecef", strokeDashArray: 4 },
    tooltip: { theme: "dark" },
  };

  const contactSeries = [totalContacts || 1, phoneContacts || 1, taggedContacts || 1, highScoreContacts || 1];
  const contactOptions = {
    chart: { type: "donut" },
    labels: ["Contacts", "Phone", "Tagged", "High Value"],
    colors: chartColors,
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    stroke: { show: false },
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col>
              <div className="h-100">
                <Section rightClickBtn={() => {}} greeting={getGreeting()} userName={userName} />
                <Row className="g-3 mb-4">
                  <Col xl={3} sm={6}>
                    <Card className="border-0 bg-soft-primary">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-primary mb-2">Total Leads</p>
                        <h4 className="fw-semibold mb-1">{totalLeads}</h4>
                        <p className="text-muted mb-0">Lead pipeline count</p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} sm={6}>
                    <Card className="border-0 bg-soft-success">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-success mb-2">New Leads</p>
                        <h4 className="fw-semibold mb-1">{newLeadsCount}</h4>
                        <p className="text-muted mb-0">Recently captured opportunities</p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} sm={6}>
                    <Card className="border-0 bg-soft-warning">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-warning mb-2">Qualified Leads</p>
                        <h4 className="fw-semibold mb-1">{qualifiedLeadsCount}</h4>
                        <p className="text-muted mb-0">Conversion-ready leads</p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} sm={6}>
                    <Card className="border-0 bg-soft-danger">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-danger mb-2">Lost Leads</p>
                        <h4 className="fw-semibold mb-1">{lostLeadsCount}</h4>
                        <p className="text-muted mb-0">Needs follow-up</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <Row className="g-3 mb-4">
                  <Col xl={8}>
                    <Card className="border-0 shadow-sm overflow-hidden">
                      <CardBody>
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div>
                            <h5 className="mb-1">Lead Trend</h5>
                            <p className="text-muted mb-0">Monthly CRM performance overview.</p>
                          </div>
                          <span className="badge bg-soft-success text-success">+24% growth</span>
                        </div>
                        <ReactApexChart options={leadTrendOptions} series={leadTrendSeries} type="area" height={320} />
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={4}>
                    <Card className="border-0 shadow-sm overflow-hidden">
                      <CardBody>
                        <div className="d-flex align-items-center justify-content-between mb-4">
                          <div>
                            <h5 className="mb-1">Contacts Breakdown</h5>
                            <p className="text-muted mb-0">Active CRM contact segments.</p>
                          </div>
                        </div>
                        <ReactApexChart options={contactOptions} series={contactSeries} type="donut" height={320} />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col xl={3} sm={6}>
                    <Card className="border-0 shadow-sm bg-soft-info">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-info mb-2">Pipeline Velocity</p>
                        <h4 className="fw-semibold mb-1">{Math.max(12, totalLeads)}</h4>
                        <p className="text-muted mb-0">Leads moving through funnel</p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} sm={6}>
                    <Card className="border-0 shadow-sm bg-soft-secondary">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-secondary mb-2">Contact Reach</p>
                        <h4 className="fw-semibold mb-1">{phoneContacts}</h4>
                        <p className="text-muted mb-0">Phone-ready contacts</p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} sm={6}>
                    <Card className="border-0 shadow-sm bg-soft-success">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-success mb-2">High Priority</p>
                        <h4 className="fw-semibold mb-1">{highScoreContacts}</h4>
                        <p className="text-muted mb-0">Important connections</p>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} sm={6}>
                    <Card className="border-0 shadow-sm bg-soft-light">
                      <CardBody>
                        <p className="text-uppercase fw-semibold text-muted mb-2">Tagged Contacts</p>
                        <h4 className="fw-semibold mb-1">{taggedContacts}</h4>
                        <p className="text-muted mb-0">Organized segments</p>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
            <RecentActivity rightColumn={false} hideRightColumn={() => {}} />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DashboardEcommerce;
